/**
 * Vonalkód / QR-kód szkenner — @zxing/browser
 * Modal-ként nyílik, kamerát használ, visszaadja a kódot.
 */
import { useState, useEffect, useRef } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import Button from './ui/Button';
import Modal from './ui/Modal';
import { useToast } from './ui/Toast';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onScan: (code: string) => void;
}

export default function BarcodeScanner({ isOpen, onClose, onScan }: Props) {
  const { toast } = useToast();
  const videoRef = useRef<HTMLVideoElement>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    let stopped = false;
    setScanning(true);

    const reader = new BrowserMultiFormatReader();
    readerRef.current = reader;

    (async () => {
      try {
        const devices = await BrowserMultiFormatReader.listVideoInputDevices();
        if (devices.length === 0) {
          toast('Nincs kamera elérhető.', 'error');
          setScanning(false);
          return;
        }

        // Hátsó kamera preferálása mobilon
        const backCamera = devices.find(d => d.label.toLowerCase().includes('back') || d.label.toLowerCase().includes('hátsó'));
        const deviceId = backCamera?.deviceId || devices[0].deviceId;

        if (videoRef.current && !stopped) {
          const controls = await reader.decodeFromVideoDevice(deviceId, videoRef.current, (result) => {
            if (result && !stopped) {
              stopped = true;
              const code = result.getText();
              controls.stop();
              onScan(code);
              onClose();
            }
          });
        }
      } catch (e: any) {
        if (!stopped) {
          toast(e.message?.includes('Permission') ? 'Kamera hozzáférés megtagadva. Engedélyezd a böngésző beállításaiban.' : 'Kamera nem elérhető.', 'error');
          setScanning(false);
        }
      }
    })();

    return () => {
      stopped = true;
      readerRef.current = null;
    };
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Vonalkód / QR-kód szkennelés">
      <div style={{ textAlign: 'center' }}>
        <>
          <video
            ref={videoRef}
            style={{
              width: '100%', maxWidth: '400px', height: 'auto',
              borderRadius: 'var(--r)', background: '#000',
              aspectRatio: '4/3',
            }}
            autoPlay playsInline muted
          />
          <p style={{ fontSize: '13px', color: 'var(--slate)', marginTop: '12px' }}>
            Tartsd a vonalkódot vagy QR-kódot a kamera elé.
          </p>
        </>
        <div style={{ marginTop: '16px' }}>
          <Button variant="ghost" onClick={onClose}>Bezárás</Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Scanner gomb — bárhol használható, megnyitja a scannert és visszaadja a kódot
 */
export function ScannerButton({ onScan, size = 'md' }: { onScan: (code: string) => void; size?: 'sm' | 'md' }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Vonalkód szkennelés"
        className="btn btn-ghost"
        style={size === 'sm' ? { padding: '6px 10px', fontSize: '14px' } : { padding: '9px 12px' }}
      >
        📷
      </button>
      <BarcodeScanner isOpen={open} onClose={() => setOpen(false)} onScan={(code) => { setOpen(false); onScan(code); }} />
    </>
  );
}
